import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  LinkOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  buildUpasargaPatch,
  createUpasarga,
  deleteUpasarga,
  getUpasarga,
  listUpasargas,
  updateUpasarga,
  type Upasarga,
} from '../api/upasargaApi';
import {
  createUpasargaSubanta,
  deleteUpasargaSubanta,
  listUpasargaSubantas,
  updateUpasargaSubanta,
  type UpasargaSubantaLink,
} from '../api/upasargaSubantaApi';
import { listSubantas, type Subanta } from '../api/subantaApi';

export function UpasargasPage() {
  const [rows, setRows] = useState<Upasarga[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Upasarga | null>(null);
  const [saving, setSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<Upasarga | null>(null);
  const [subantaRows, setSubantaRows] = useState<Subanta[]>([]);
  const [upasargaSubantaLinks, setUpasargaSubantaLinks] = useState<UpasargaSubantaLink[]>([]);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkTargetUpasarga, setLinkTargetUpasarga] = useState<Upasarga | null>(null);
  const [linkSaving, setLinkSaving] = useState(false);
  const [form] = Form.useForm<{ name: string; description: string }>();
  const [linkForm] = Form.useForm<{ subantaId: number }>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [upas, subantas, links] = await Promise.all([
        listUpasargas(),
        listSubantas(),
        listUpasargaSubantas(),
      ]);
      setRows(upas);
      setSubantaRows(subantas);
      setUpasargaSubantaLinks(links);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load upasargas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ name: '', description: '' });
    setModalOpen(true);
  };

  const openEdit = (record: Upasarga) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description ?? '',
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing) {
        const patch = buildUpasargaPatch(editing, values);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updateUpasarga(editing.id, patch);
        message.success('Upasarga updated');
      } else {
        await createUpasarga({
          name: values.name,
          description: values.description.trim() === '' ? undefined : values.description,
        });
        message.success('Upasarga created');
      }
      closeModal();
      await load();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setDetailOpen(true);
    setDetail(null);
    try {
      setDetail(await getUpasarga(id));
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Failed to load upasarga');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteUpasarga(id);
      message.success('Upasarga deleted');
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
    }
  };

  const openLinkModal = (upasarga: Upasarga) => {
    setLinkTargetUpasarga(upasarga);
    linkForm.resetFields();
    const cur = upasargaSubantaLinks.find((l) => l.upasargaId === upasarga.id);
    linkForm.setFieldsValue({ subantaId: cur?.subantaId });
    setLinkModalOpen(true);
  };

  const closeLinkModal = () => {
    setLinkModalOpen(false);
    setLinkTargetUpasarga(null);
    linkForm.resetFields();
  };

  const linkSubantaSelectOptions = useMemo(() => {
    if (!linkTargetUpasarga) return [];
    const current = upasargaSubantaLinks.find((l) => l.upasargaId === linkTargetUpasarga.id);
    const takenSubantaIds = new Set(
      upasargaSubantaLinks
        .filter((l) => current == null || l.id !== current.id)
        .map((l) => l.subantaId)
    );
    return subantaRows
      .filter((s) => !takenSubantaIds.has(s.id))
      .map((s) => ({
        value: s.id,
        label: `${s.word} (#${s.id}) — ${s.iast}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [linkTargetUpasarga, subantaRows, upasargaSubantaLinks]);

  const submitLinkModal = async () => {
    if (!linkTargetUpasarga) return;
    try {
      const values = await linkForm.validateFields();
      const subantaId = values.subantaId;
      const existing = upasargaSubantaLinks.find((l) => l.upasargaId === linkTargetUpasarga.id);
      setLinkSaving(true);
      if (existing) {
        if (existing.subantaId === subantaId) {
          message.info('No changes to save');
          closeLinkModal();
          return;
        }
        await updateUpasargaSubanta(existing.id, { subantaId });
        message.success('Upasarga–subanta link updated');
      } else {
        await createUpasargaSubanta({
          upasargaId: linkTargetUpasarga.id,
          subantaId,
        });
        message.success('Upasarga–subanta link created');
      }
      await load();
      closeLinkModal();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Save failed');
    } finally {
      setLinkSaving(false);
    }
  };

  const removeUpasargaSubantaLink = async () => {
    if (!linkTargetUpasarga) return;
    const existing = upasargaSubantaLinks.find((l) => l.upasargaId === linkTargetUpasarga.id);
    if (!existing) return;
    setLinkSaving(true);
    try {
      await deleteUpasargaSubanta(existing.id);
      message.success('Link removed');
      await load();
      closeLinkModal();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax?.response?.data?.message || ax?.message || 'Delete failed');
    } finally {
      setLinkSaving(false);
    }
  };

  const linkForUpasarga = useCallback(
    (upasargaId: number) => upasargaSubantaLinks.find((l) => l.upasargaId === upasargaId),
    [upasargaSubantaLinks]
  );

  const subantaLabel = useCallback(
    (subantaId: number) => {
      const s = subantaRows.find((x) => x.id === subantaId);
      return s ? `${s.word} (#${s.id})` : `#${subantaId}`;
    },
    [subantaRows]
  );

  const columns: ColumnsType<Upasarga> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Name', dataIndex: 'name', ellipsis: true, width: 160 },
    { title: 'Description', dataIndex: 'description', ellipsis: true },
    {
      title: 'Linked subanta',
      key: 'subantaLink',
      width: 220,
      ellipsis: true,
      render: (_, record) => {
        const lnk = linkForUpasarga(record.id);
        return (
          <Space size="small" wrap>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {lnk ? subantaLabel(lnk.subantaId) : '—'}
            </Typography.Text>
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => openLinkModal(record)}
            >
              {lnk ? 'Edit' : 'Link'}
            </Button>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 240,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => void openDetail(record.id)}
          >
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this upasarga?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => void handleDelete(record.id)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Upasarga"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add upasarga
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Prefix/particle names — CRUD for <code>/upasarga</code> on{' '}
        {process.env.REACT_APP_DICTIONARY_API_URL || 'https://koshaapidev.vag.org.np'}. Each row
        may have at most one <code>/upasarga-subanta</code> link (one-to-one with a subanta).
      </Typography.Paragraph>
      <Table<Upasarga>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        scroll={{ x: 960 }}
      />

      <Modal
        title="Upasarga details"
        open={detailOpen}
        onCancel={() => {
          setDetailOpen(false);
          setDetail(null);
        }}
        footer={null}
        width={560}
      >
        {detailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : detail ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text>
              <strong>ID:</strong> {detail.id}
            </Typography.Text>
            <Typography.Text>
              <strong>Name:</strong> {detail.name}
            </Typography.Text>
            <Typography.Paragraph style={{ marginBottom: 0 }}>
              <Typography.Text strong>Description:</Typography.Text>{' '}
              {detail.description?.trim() ? detail.description : '—'}
            </Typography.Paragraph>
            <Typography.Title level={5} style={{ marginTop: 16, marginBottom: 8 }}>
              Subanta link (one-to-one)
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              <code>GET/POST /upasarga-subanta</code> — each upasarga and each subanta can appear in
              at most one link.
            </Typography.Paragraph>
            {(() => {
              const lnk = linkForUpasarga(detail.id);
              return (
                <>
                  <Typography.Paragraph style={{ marginBottom: 8 }}>
                    {lnk ? (
                      <>
                        <Typography.Text strong>Linked:</Typography.Text>{' '}
                        {subantaLabel(lnk.subantaId)} — IAST:{' '}
                        {subantaRows.find((s) => s.id === lnk.subantaId)?.iast ?? '—'}
                      </>
                    ) : (
                      <Typography.Text type="secondary">No link yet.</Typography.Text>
                    )}
                  </Typography.Paragraph>
                  <Button
                    type="primary"
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={() => openLinkModal(detail)}
                  >
                    {lnk ? 'Edit subanta link' : 'Link subanta'}
                  </Button>
                </>
              );
            })()}
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={
          linkTargetUpasarga
            ? upasargaSubantaLinks.some((l) => l.upasargaId === linkTargetUpasarga.id)
              ? `Edit subanta link — ${linkTargetUpasarga.name}`
              : `Link subanta — ${linkTargetUpasarga.name}`
            : 'Upasarga–subanta link'
        }
        open={linkModalOpen}
        onCancel={closeLinkModal}
        destroyOnClose
        width={520}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            {linkTargetUpasarga &&
            upasargaSubantaLinks.some((l) => l.upasargaId === linkTargetUpasarga.id) ? (
              <Popconfirm
                title="Remove this link?"
                okText="Remove"
                okButtonProps={{ danger: true }}
                onConfirm={() => void removeUpasargaSubantaLink()}
              >
                <Button danger loading={linkSaving}>
                  Remove link
                </Button>
              </Popconfirm>
            ) : null}
            <Button onClick={closeLinkModal}>Cancel</Button>
            <Button
              type="primary"
              loading={linkSaving}
              disabled={subantaRows.length === 0}
              onClick={() => void submitLinkModal()}
            >
              Save
            </Button>
          </Space>
        }
      >
        {subantaRows.length === 0 ? (
          <Typography.Text type="secondary">
            No subantas loaded. Add subantas on the Subanta page first.
          </Typography.Text>
        ) : (
          <Form form={linkForm} layout="vertical" preserve={false}>
            <Form.Item
              name="subantaId"
              label="Subanta"
              rules={[{ required: true, message: 'Choose a subanta' }]}
            >
              <Select
                options={linkSubantaSelectOptions}
                showSearch
                optionFilterProp="label"
                placeholder="Select subanta"
                style={{ minWidth: '100%' }}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title={editing ? 'Edit upasarga' : 'Add upasarga'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void onSubmit()}
        confirmLoading={saving}
        destroyOnClose
        width={520}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="e.g. प्र" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
